import { RowButton } from "@/components/ui/pressable";
import { useEffect, useState, type ReactNode } from "react";
import {
  FieldTitle,
  useInput,
  useResourceContext,
  type InputProps,
} from "ra-core";
import { ExternalLink, Loader2, MapPin, Phone, Star } from "lucide-react";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "@/components/admin/form";
import { InputHelperText } from "@/components/admin/input-helper-text";
import { Input } from "@/components/ui/input";
import { FloatingFieldShell } from "@/components/ui/floating-field";
import { floatingFieldPlaceholder } from "@/components/ui/floating-field-utils";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  fetchGooglePlaceDetails,
  fetchPlacesAutocomplete,
  GooglePlacesUnavailableError,
  isGooglePlacesEnabled,
  placePhotoMediaUrl,
  type GooglePlaceDetails,
  type GooglePlacesAutocompleteMode,
} from "@/lib/googlePlaces";

type PlaceSuggestionRow = {
  placeId: string;
  text: string;
  name?: string;
  address?: string;
  phone?: string;
  website?: string;
  mapsUri?: string;
  photoUrl?: string | null;
  rating?: number;
  reviewCount?: number;
};

const withTimeout = async <T,>(
  promise: Promise<T>,
  ms: number,
  signal?: AbortSignal,
): Promise<T | null> => {
  if (signal?.aborted) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

export type GooglePlacesAutocompleteInputProps = Omit<InputProps, "source"> &
  Pick<InputProps, "source"> & {
    mode: GooglePlacesAutocompleteMode;
    onPlaceDetails?: (details: GooglePlaceDetails) => void;
    onManualChange?: () => void;
    placeholder?: string;
    multiline?: boolean;
    className?: string;
    suggestionHeader?: ReactNode;
    disabled?: boolean;
    readOnly?: boolean;
    labelVariant?: "default" | "floating";
    /**
     * City already on the form (e.g. company_city) — appended to server
     * Text Search only; does not change the visible input.
     */
    nearbyCity?: string | null;
  };

export const GooglePlacesAutocompleteInput = ({
  source,
  label,
  helperText,
  validate,
  mode,
  onPlaceDetails,
  onManualChange,
  placeholder,
  multiline = false,
  className,
  suggestionHeader,
  disabled,
  readOnly,
  labelVariant = "floating",
  nearbyCity,
}: GooglePlacesAutocompleteInputProps) => {
  const resource = useResourceContext();
  const { companyCity, companyState } = useConfigurationContext();
  const { id, field, isRequired } = useInput({
    source,
    label,
    helperText,
    validate,
    disabled,
    readOnly,
  });

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<PlaceSuggestionRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const query = String(field.value ?? "").trim();
  const placesEnabled = isGooglePlacesEnabled();
  const canShowSuggestions = placesEnabled && query.length >= 3;
  const nearbyCityTrim = String(nearbyCity ?? "").trim() || null;
  const biasCity =
    mode === "business" ? String(companyCity ?? "").trim() || null : null;
  const biasState =
    mode === "business" ? String(companyState ?? "").trim() || null : null;

  useEffect(() => {
    if (!placesEnabled || query.length < 3) {
      setSuggestions([]);
      setIsLoading(false);
      setFetchError(null);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        // Bias geocode runs on the edge from biasCity/biasState — don't block UI.
        const next = await withTimeout(
          fetchPlacesAutocomplete(query, mode, {
            signal: controller.signal,
            nearbyCity: mode === "business" ? nearbyCityTrim : null,
            biasCity,
            biasState,
          }),
          8_000,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        if (!next) {
          setSuggestions([]);
          setFetchError("Search timed out. Try again.");
          setIsLoading(false);
          return;
        }

        const rows: PlaceSuggestionRow[] = next.map((item) => ({
          placeId: item.placeId,
          text: item.text,
          name: item.text.split(",")[0]?.trim() || item.text,
        }));
        setSuggestions(rows);
        setFetchError(
          rows.length === 0 ? "No Google results for this search." : null,
        );
        setIsLoading(false);

        // Enrich in background (don't keep "Searching…" spinner).
        if (mode !== "business" || rows.length === 0) return;

        const enriched = await Promise.all(
          rows.slice(0, 3).map(async (row) => {
            try {
              const details = await withTimeout(
                fetchGooglePlaceDetails(row.placeId, controller.signal),
                2500,
                controller.signal,
              );
              if (!details) return row;
              return {
                ...row,
                name: details.name || row.name,
                address: details.formattedAddress || undefined,
                phone: details.phone || undefined,
                website: details.website || undefined,
                mapsUri: details.googleMapsUri || undefined,
                photoUrl: placePhotoMediaUrl(details.photoName),
                rating: details.rating,
                reviewCount: details.reviewCount,
              } satisfies PlaceSuggestionRow;
            } catch {
              return row;
            }
          }),
        );
        if (controller.signal.aborted) return;
        setSuggestions((prev) => {
          const byId = new Map(enriched.map((row) => [row.placeId, row]));
          return prev.map((row) => byId.get(row.placeId) ?? row);
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        setSuggestions([]);
        if (error instanceof GooglePlacesUnavailableError) {
          setFetchError(error.message);
        } else {
          setFetchError(
            "Could not reach Google Places. You can still type manually.",
          );
        }
        setIsLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, mode, placesEnabled, nearbyCityTrim, biasCity, biasState]);

  const handlePick = async (item: PlaceSuggestionRow) => {
    const displayValue =
      item.name?.trim() || item.text.split(",")[0]?.trim() || item.text;
    field.onChange(displayValue);
    setOpen(false);

    if (!onPlaceDetails) return;

    setIsFetchingDetails(true);
    try {
      const details = await fetchGooglePlaceDetails(item.placeId);
      if (details) {
        onPlaceDetails(details);
      }
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const defaultPlaceholder =
    mode === "business"
      ? "Search for a business on Google…"
      : "Search for an address on Google…";

  const panelContent = (
    <>
      {suggestionHeader}
      {suggestionHeader ? <div className="my-2 border-t" /> : null}
      <div className="mb-1 flex items-center gap-1 px-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
        <MapPin className="size-3" />
        Google suggestions
      </div>
      {isLoading ? (
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          Searching…
        </div>
      ) : suggestions.length > 0 ? (
        suggestions.map((item) => (
          <RowButton
            layout="none"
            density="none"
            className="mb-0.5 flex w-full gap-2.5 rounded px-2 py-1.5 text-left hover:bg-muted/50"
            key={item.placeId}
            onMouseDown={(event) => {
              event.preventDefault();
              void handlePick(item);
            }}
          >
            {mode === "business" ? (
              <>
                <div className="bg-muted mt-0.5 size-10 shrink-0 overflow-hidden rounded-md">
                  {item.photoUrl ? (
                    <img
                      src={item.photoUrl}
                      alt=""
                      className="size-full object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="text-muted-foreground flex size-full items-center justify-center text-xs font-semibold">
                      {(item.name ?? "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {item.name ?? item.text}
                  </div>
                  {item.address ? (
                    <div className="text-muted-foreground truncate text-xs">
                      {item.address}
                    </div>
                  ) : (
                    <div className="text-muted-foreground truncate text-xs">
                      {item.text}
                    </div>
                  )}
                  <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs">
                    {typeof item.rating === "number" ||
                    typeof item.reviewCount === "number" ? (
                      <span className="inline-flex items-center gap-0.5">
                        <Star className="size-2.5 fill-warning text-warning-text" />
                        {typeof item.rating === "number"
                          ? item.rating.toFixed(1)
                          : "—"}
                        {typeof item.reviewCount === "number"
                          ? ` (${item.reviewCount})`
                          : null}
                      </span>
                    ) : null}
                    {item.phone ? (
                      <span className="inline-flex items-center gap-0.5">
                        <Phone className="size-2.5" />
                        {item.phone}
                      </span>
                    ) : null}
                    {item.website ? (
                      <span className="truncate">{item.website}</span>
                    ) : null}
                    {item.mapsUri ? (
                      <a
                        href={item.mapsUri}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-0.5 underline-offset-2 hover:underline"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <ExternalLink className="size-2.5" />
                        Google
                      </a>
                    ) : null}
                  </div>
                </div>
              </>
            ) : (
              <span className="text-sm">{item.text}</span>
            )}
          </RowButton>
        ))
      ) : (
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          {fetchError ??
            (query.length >= 3
              ? "No Google Places match. Service-area businesses without a street address often do not appear — keep typing manually."
              : "Type at least 3 characters.")}
        </div>
      )}
    </>
  );

  const useFloating = labelVariant === "floating" && label !== false;
  const floatingActive =
    focused || open || String(field.value ?? "").length > 0;
  const inputPlaceholder = useFloating
    ? floatingFieldPlaceholder(
        floatingActive,
        placeholder ?? defaultPlaceholder,
      )
    : (placeholder ?? defaultPlaceholder);

  const fieldControl = (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverAnchor asChild>
        <div className="relative w-full">
          {multiline ? (
            <Textarea
              {...field}
              value={field.value ?? ""}
              disabled={disabled}
              readOnly={readOnly}
              rows={2}
              placeholder={inputPlaceholder}
              className={cn(
                "max-h-24 min-h-9 resize-y py-2 leading-snug",
                useFloating &&
                  "min-h-[5.5rem] rounded-md border-0 bg-transparent px-3 py-2 shadow-none focus-visible:ring-0",
              )}
              onFocus={() => {
                setFocused(true);
                if (canShowSuggestions) setOpen(true);
              }}
              onBlur={() => setFocused(false)}
              onChange={(event) => {
                field.onChange(event.target.value);
                onManualChange?.();
                if (event.target.value.trim().length >= 3) {
                  setOpen(true);
                } else {
                  setOpen(false);
                }
              }}
            />
          ) : (
            <Input
              {...field}
              value={field.value ?? ""}
              disabled={disabled}
              readOnly={readOnly}
              placeholder={inputPlaceholder}
              className={cn(
                useFloating &&
                  "h-9 border-0 bg-transparent px-3 shadow-none focus-visible:ring-0",
              )}
              onFocus={() => {
                setFocused(true);
                if (canShowSuggestions) setOpen(true);
              }}
              onBlur={() => {
                setFocused(false);
                field.onBlur();
              }}
              onChange={(event) => {
                field.onChange(event.target.value);
                onManualChange?.();
                if (event.target.value.trim().length >= 3) {
                  setOpen(true);
                } else {
                  setOpen(false);
                }
              }}
            />
          )}
          {isFetchingDetails ? (
            <Loader2
              className={cn(
                "pointer-events-none absolute right-2 size-4 animate-spin text-muted-foreground",
                useFloating ? "top-2.5" : "top-2.5",
              )}
            />
          ) : null}
        </div>
      </PopoverAnchor>
      {open && canShowSuggestions ? (
        <PopoverContent
          className="z-[200] w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
          align="start"
          sideOffset={6}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onWheel={(event) => event.stopPropagation()}
        >
          <div
            className="max-h-72 overflow-y-auto overscroll-contain p-2"
            onWheel={(event) => event.stopPropagation()}
          >
            {panelContent}
          </div>
        </PopoverContent>
      ) : null}
    </Popover>
  );

  const labelNode =
    label !== false ? (
      <FieldTitle
        label={label}
        source={source}
        resource={resource}
        isRequired={useFloating ? false : isRequired}
      />
    ) : null;

  return (
    <FormField
      id={id}
      name={field.name}
      className={cn(useFloating && "gap-1.5", className)}
    >
      {useFloating ? (
        <FloatingFieldShell
          active={floatingActive}
          label={labelNode}
          htmlFor={id}
          required={isRequired}
          labelAlign={multiline ? "top" : "center"}
          className={multiline ? "min-h-[5.5rem] items-stretch" : undefined}
        >
          <FormControl>{fieldControl}</FormControl>
        </FloatingFieldShell>
      ) : (
        <>
          {labelNode ? <FormLabel>{labelNode}</FormLabel> : null}
          <FormControl>{fieldControl}</FormControl>
        </>
      )}
      <InputHelperText
        helperText={
          helperText ??
          (placesEnabled
            ? mode === "business"
              ? "Pick a result to fill website, phone, and address."
              : "Pick an address or type manually."
            : "Add VITE_GOOGLE_PLACES_API_KEY to .env and restart Vite.")
        }
      />
      <FormError />
    </FormField>
  );
};
